// depositAgreementClient — authed staff client for the Deposit Agreement send
// surface (/send/deposit).
//
// Two endpoints, both Bearer-authed via the shared `api` helper:
//   • GET  /api/deposit-agreement/occupancies?includeNotice=<bool>
//       → { occupancies: DepositOccupancyRow[] }
//     Lists the current/past occupancies eligible for a deposit-agreement send.
//     The backend gates this behind a feature flag; when it's off the route
//     answers 503 — we surface that as a `notEnabled` flag rather than throwing,
//     so the page can show a friendly "not turned on yet" state.
//
//   • POST /api/deposit-agreement/:occupancyId/initiate
//       → { occupancyId, sent, recipients: [{ key, name, kind, hasEmail }] }
//     Sends the recipient-designation intake to the occupancy's residents +
//     co-signers and returns how many links went out.
//
// Wire shapes are PINNED to the backend contract (see the Send-tab report).

import { api, ApiError } from './api';

/** One resident/co-signer on a deposit-eligible occupancy. */
export interface DepositOccupancyResident {
  /** AppFolio tenant id. */
  tenantId: string;
  name: string;
  email: string | null;
}

/** A current/past occupancy eligible for a deposit-agreement send. */
export interface DepositOccupancyRow {
  /** Stable occupancy id — the path key for `initiate`. */
  occupancyId: string;
  unitId: string;
  /** Null when AppFolio hasn't mirrored a property id for the unit. */
  propertyId: string | null;
  /** Human-readable unit/property address. */
  address: string;
  /** 'Past' = lease already ended; 'Notice' = on notice but not yet ended. */
  status: 'Past' | 'Notice';
  /** ISO date the lease ended (or ends, for Notice). */
  leaseEndDate: string;
  /** Days since the lease ended (negative/0 for future-dated Notice rows). */
  daysSinceLeaseEnd: number;
  /** yyyy-mm-dd AppFolio move-out date ('' when none) — the date a Notice row shows (WEL-165). */
  moveOutDate?: string;
  /** How many signers (residents + co-signers) get the intake. */
  signerCount: number;
  /** Of `signerCount`, how many are co-signers. */
  cosignerCount: number;
  /** The residents + co-signers who would receive the intake. */
  residents: DepositOccupancyResident[];
  /** WEL-121: present when an RDA intake was ALREADY sent for this occupancy — the
   *  page shows "Send again" + a sent indicator. Absent ⇒ never sent. */
  priorSend?: {
    status: 'initiated' | 'locked' | 'sent';
    initiatedAt: string;
    agreementSentAt?: string;
    round: number;
    /** WEL-125: set when staff cancelled this RDA — the row shows a "Cancelled"
     *  indicator and re-enables a fresh "Send deposit agreement". */
    cancelledAt?: string;
  };
}

export interface ListDepositOccupanciesResult {
  occupancies: DepositOccupancyRow[];
  /**
   * Whether SENDING is enabled (MOVEOUT_FUNDS_SEND_ENABLED). The list itself is
   * read-only and always returned; when this is false the page shows the rows but
   * disables the Send buttons behind a "not enabled yet" banner.
   */
  sendEnabled: boolean;
  /** True only when the service itself is unavailable (503 — e.g. PG mirror off). */
  notEnabled: boolean;
}

/**
 * List deposit-agreement-eligible occupancies (read-only — always available when
 * the tenants mirror is up). A 503 means the service itself is unavailable; we
 * resolve with `notEnabled: true` rather than throwing so the page degrades
 * gracefully. `sendEnabled` reflects whether the Send action is turned on.
 */
export async function listDepositOccupancies(
  includeNotice: boolean,
): Promise<ListDepositOccupanciesResult> {
  try {
    const raw = await api.get<{ occupancies?: DepositOccupancyRow[]; sendEnabled?: boolean }>(
      `/api/deposit-agreement/occupancies?includeNotice=${includeNotice ? 'true' : 'false'}`,
    );
    return {
      occupancies: raw.occupancies ?? [],
      sendEnabled: raw.sendEnabled ?? false,
      notEnabled: false,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      return { occupancies: [], sendEnabled: false, notEnabled: true };
    }
    throw err;
  }
}

/** One recipient the intake was sent to, echoed back by `initiate`. */
export interface DepositInitiateRecipient {
  key: string;
  name: string;
  kind: string;
  hasEmail: boolean;
}

export interface InitiateDepositAgreementResult {
  occupancyId: string;
  /** How many intake links actually went out. */
  sent: number;
  recipients: DepositInitiateRecipient[];
}

/**
 * Send the recipient-designation intake for one occupancy's deposit agreement.
 * `:occupancyId` is the row's `occupancyId`.
 */
export async function initiateDepositAgreement(
  occupancyId: string,
): Promise<InitiateDepositAgreementResult> {
  const raw = await api.post<{
    occupancyId?: string;
    sent?: number;
    recipients?: DepositInitiateRecipient[];
  }>(`/api/deposit-agreement/${encodeURIComponent(occupancyId)}/initiate`);
  return {
    occupancyId: raw.occupancyId ?? occupancyId,
    sent: raw.sent ?? 0,
    recipients: raw.recipients ?? [],
  };
}

export interface CancelDepositAgreementResult {
  cancelled: boolean;
  /** DocuSeal submission id that was voided (null if nothing was out for signing). */
  voidedSubmissionId: number | null;
  /** How many recipients got the cancellation notice. */
  recipientsEmailed: number;
  /** True when the RDA was already cancelled (idempotent no-op). */
  alreadyCancelled?: boolean;
}

/**
 * Cancel the whole RDA for one occupancy (WEL-125). Voids the open agreement if
 * it's out for signing, stops a not-yet-completed intake, and emails the
 * recipients a cancellation notice. The occupancy stays re-sendable afterward.
 * `:occupancyId` is the row's `occupancyId` (= the moveout-funds groupId).
 */
export async function cancelDepositAgreement(
  occupancyId: string,
): Promise<CancelDepositAgreementResult> {
  const raw = await api.post<{
    cancelled?: boolean;
    voidedSubmissionId?: number | null;
    recipientsEmailed?: number;
    alreadyCancelled?: boolean;
  }>(`/api/moveout-funds/${encodeURIComponent(occupancyId)}/cancel`);
  return {
    cancelled: raw.cancelled ?? false,
    voidedSubmissionId: raw.voidedSubmissionId ?? null,
    recipientsEmailed: raw.recipientsEmailed ?? 0,
    ...(raw.alreadyCancelled !== undefined ? { alreadyCancelled: raw.alreadyCancelled } : {}),
  };
}
